'use client';

import type { INavListItem } from '@/api/types/public';
import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import ImageNavigationNav from './ImageNavigationNav.pc';
import {
  canDisplayTextImageNavigation,
  getImageNavigationPreferredWidth,
} from './navigationLayoutPriority';
import styles from './index.module.scss';
import TextNav from './TextNav';

interface TextImageNavigationLayoutProps {
  navigationItems: INavListItem[];
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
  isActive: boolean;
}

const getNumericStyleValue = (value: string) => Number.parseFloat(value) || 0;

const TextImageNavigationLayout = ({
  navigationItems,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
  isActive,
}: TextImageNavigationLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const textNavigationItems = navigationItems.filter((item) => item.template_id === 2);
  const imageNavigationItems = navigationItems.filter((item) => item.template_id === 3);
  const textNavigationIndexMap = new Map<INavListItem, number>();
  textNavigationItems.forEach((item, index) => textNavigationIndexMap.set(item, index));

  const textNavigationCount = textNavigationItems.length;
  const imageNavigationCount = imageNavigationItems.length;
  const [showImageNavigation, setShowImageNavigation] = useState(true);

  useLayoutEffect(() => {
    if (!isActive) return;

    const layout = layoutRef.current;
    if (!layout) return;

    const updateImageNavigationVisibility = () => {
      const computedStyle = window.getComputedStyle(layout);
      const availableWidth =
        layout.clientWidth -
        getNumericStyleValue(computedStyle.paddingLeft) -
        getNumericStyleValue(computedStyle.paddingRight);
      const moduleGap = getNumericStyleValue(computedStyle.columnGap || computedStyle.gap);
      const textNavigationWidth = Array.from(
        layout.querySelectorAll<HTMLElement>('[data-text-image-navigation-text]'),
      ).reduce((totalWidth, element) => totalWidth + element.getBoundingClientRect().width, 0);
      const nextVisibility = canDisplayTextImageNavigation({
        availableWidth,
        textNavigationWidth,
        textNavigationCount,
        imageNavigationCount,
        moduleGap,
      });

      setShowImageNavigation((currentVisibility) =>
        currentVisibility === nextVisibility ? currentVisibility : nextVisibility,
      );
    };

    updateImageNavigationVisibility();

    const resizeObserver = new ResizeObserver(updateImageNavigationVisibility);
    resizeObserver.observe(layout);
    layout
      .querySelectorAll<HTMLElement>('[data-text-image-navigation-text]')
      .forEach((element) => resizeObserver.observe(element));

    return () => resizeObserver.disconnect();
  }, [imageNavigationCount, isActive, textNavigationCount]);

  return (
    <div ref={layoutRef} className={styles.textImageNavigationLayout}>
      {navigationItems.map((item, navigationIndex) => {
        if (item.template_id === 2) {
          const textNavigationIndex = textNavigationIndexMap.get(item) ?? 0;

          return (
            <div
              key={`${item.id}-${item.template_id}-${navigationIndex}`}
              className={styles.textImageNavigationTextColumn}
              data-text-image-navigation-text
            >
              <TextNav
                item={item}
                firstLevelIndex={firstLevelIndex}
                firstLevelTitle={firstLevelTitle}
                secondLevelIndex={textNavigationIndex}
                isLast={textNavigationIndex === textNavigationCount - 1}
                isDarkMode={isDarkMode}
                isActive={isActive}
              />
            </div>
          );
        }

        if (item.template_id !== 3 || !showImageNavigation) return null;

        const imageItemCount =
          item.content?.list?.filter((listItem) => Boolean(listItem.jump_image)).length || 0;
        const preferredWidth = getImageNavigationPreferredWidth(imageItemCount);

        return (
          <div
            key={`${item.id}-${item.template_id}-${navigationIndex}`}
            className={styles.textImageNavigationImageModule}
            style={
              {
                '--text-image-navigation-preferred-width': `${preferredWidth}px`,
              } as CSSProperties
            }
          >
            <ImageNavigationNav
              item={item}
              firstLevelIndex={firstLevelIndex}
              firstLevelTitle={firstLevelTitle}
              isDarkMode={isDarkMode}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TextImageNavigationLayout;
